import { Component, Inject, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { UserProfile } from '@core/models';
import { PasswordValidator } from '@core/helpers/password_validator';
import { MatchingPasswordsWithParams } from '@core/validators/password-matching.validator';
import { UpdatePasswordModel } from '@root/app/authorization/models';
import { AuthStore } from '@root/app/store/auth.store';
import {
  DialogShellComponent,
  FilledButtonComponent,
  TextButtonComponent,
  TextFieldComponent,
} from '@custom-components';

@Component({
  selector: 'xemex-change-password-dialog',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    DialogShellComponent,
    TextFieldComponent,
    FilledButtonComponent,
    TextButtonComponent,
  ],
  templateUrl: './change-password-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './change-password-dialog.component.scss',
})
export class ChangePasswordDialogComponent {
  private readonly authStore = inject(AuthStore);

  readonly securityForm = new FormGroup(
    {
      oldPassword: new FormControl('', Validators.required),
      newPassword: new FormControl('', [Validators.required, PasswordValidator]),
      confirmNewPassword: new FormControl('', [Validators.required, PasswordValidator]),
    },
    {
      validators: MatchingPasswordsWithParams({
        passwordKey: 'newPassword',
        confirmPasswordKey: 'confirmNewPassword',
      }),
    },
  );

  private readonly userProfile: UserProfile | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { userProfile: UserProfile | undefined },
    private readonly dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
  ) {
    this.userProfile = data.userProfile;
  }

  hasError(controlName: keyof typeof this.securityForm.controls, errorCode: string): boolean {
    const control = this.securityForm.controls[controlName];
    return control.hasError(errorCode) && (control.touched || control.dirty);
  }

  onUpdatePassword() {
    const userEmail = this.userProfile?.email;
    if (this.securityForm.invalid || !userEmail) {
      this.securityForm.markAllAsTouched();
      return;
    }

    const resetPassword: UpdatePasswordModel = {
      user_email: userEmail,
      current_password: this.securityForm.controls.oldPassword.value ?? '',
      new_password: this.securityForm.controls.newPassword.value ?? '',
    };

    this.authStore.updatePasswordMethod(resetPassword);
    this.dialogRef.close();
  }

  onCancel() {
    this.dialogRef.close();
  }
}
